// src/components/onboarding/LanguageGateScreen.tsx
//
// First-launch language gate. Two phases:
//
//   PHASE 1 — Hero
//     Logo + huge "Welcome" word that crossfades through the most
//     common languages. Subtle "tap to begin" hint. Tapping
//     anywhere on the screen advances to phase 2.
//
//   PHASE 2 — Picker
//     Logo retained, "Choose your language" prompt, then a "cloud"
//     of prominent languages (varied pill sizes, top languages
//     bigger), and below it a scrollable A-Z list of every
//     supported language. The picker UI lives in
//     `LanguagePickerSheet` so the topbar flag-icon can re-use it
//     for mid-session language changes.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography, ButtonBase } from '@mui/material';
import { Logo } from '../Logo';
import {
  ROTATION_LANGUAGES,
  LanguageEntry,
} from '../../i18n/languages';
import { LanguagePickerSheet } from '../i18n/LanguagePickerSheet';
import { useT } from '../../i18n/useT';

interface LanguageGateScreenProps {
  /** Called with the selected language. Parent persists + dismisses. */
  onSelect: (entry: LanguageEntry) => void;
}

const ROTATION_INTERVAL_MS = 2400;
const ROTATION_FADE_MS = 700;

export const LanguageGateScreen: React.FC<LanguageGateScreenProps> = ({
  onSelect,
}) => {
  const t = useT();
  const [phase, setPhase] = useState<'hero' | 'picker'>('hero');
  const [rotIndex, setRotIndex] = useState(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  // Lazy-load Fraunces from Google Fonts on mount. Idempotent —
  // checks for an existing tag before injecting. Stays loaded
  // after dismiss; the standby screen and picker also use it but
  // inherit this load.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const id = 'lang-gate-font-link';
    if (document.getElementById(id)) return;

    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://fonts.gstatic.com';
    preconnect.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect);

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Fraunces:wght@400;500;600&display=swap';
    document.head.appendChild(link);
  }, []);

  // Rotation timer. Stops once we're in the picker so the off-
  // screen hero phase doesn't waste re-renders.
  useEffect(() => {
    if (phase !== 'hero') return;
    const id = window.setInterval(() => {
      setRotIndex((i) => (i + 1) % ROTATION_LANGUAGES.length);
    }, ROTATION_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [phase]);

  const currentRot = ROTATION_LANGUAGES[rotIndex];

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: '#FAFAF7',
        zIndex: 2000,
        overflowY: phase === 'picker' ? 'auto' : 'hidden',
        backgroundImage:
          'radial-gradient(ellipse 60% 40% at 20% 15%, rgba(15,76,117,0.06), transparent 70%), radial-gradient(ellipse 50% 35% at 85% 90%, rgba(224,122,95,0.07), transparent 70%)',
      }}
    >
      {phase === 'hero' ? (
        <HeroPhase
          word={currentRot}
          onTap={() => setPhase('picker')}
        />
      ) : (
        <Box sx={{
          animation: 'gatePhaseIn 360ms ease-out both',
          '@keyframes gatePhaseIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 },
          },
        }}>
          <LanguagePickerSheet
            header={<Logo size={28} alt={t("Instant Tours")} />}
            onSelect={onSelect}
          />
        </Box>
      )}
    </Box>
  );
};

interface HeroPhaseProps {
  word: LanguageEntry;
  onTap: () => void;
}

const HeroPhase: React.FC<HeroPhaseProps> = ({ word, onTap }) => {
  const t = useT();
  return (
    <ButtonBase
      onClick={onTap}
      aria-label={t("Tap to choose your language")}
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        cursor: 'pointer',
        '& .MuiTouchRipple-root': { display: 'none' },
      }}
    >
      <Stack alignItems="center" spacing={6} sx={{ px: 3 }}>
        <Box sx={{ opacity: 0.85 }}>
          <Logo size={36} alt={t("Instant Tours")} />
        </Box>

        <Box
          sx={{
            position: 'relative',
            height: { xs: 96, sm: 132 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <RotatingWord key={word.code} word={word} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 13,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'rgba(26, 26, 26, 0.55)',
              fontWeight: 500,
            }}
          >
            
            {t("tap to begin")}
          </Typography>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#0F4C75',
              animation: 'gateDotPulse 1800ms ease-in-out infinite',
              '@keyframes gateDotPulse': {
                '0%, 100%': { opacity: 0.25, transform: 'scale(1)' },
                '50%': { opacity: 1, transform: 'scale(1.3)' },
              },
            }}
          />
        </Box>
      </Stack>
    </ButtonBase>
  );
};

const RotatingWord: React.FC<{ word: LanguageEntry }> = ({ word }) => {
  return (
    <Typography
      component="span"
      dir={word.rtl ? 'rtl' : 'ltr'}
      sx={{
        fontFamily: '"Fraunces", "Iowan Old Style", "Palatino", Georgia, serif',
        fontWeight: 500,
        fontSize: { xs: 56, sm: 84, md: 96 },
        lineHeight: 1.05,
        letterSpacing: '-0.02em',
        color: '#1A1A1A',
        animation: `gateWordEnter ${ROTATION_FADE_MS}ms ease-out both`,
        '@keyframes gateWordEnter': {
          '0%': { opacity: 0, transform: 'translateY(8px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
        userSelect: 'none',
      }}
    >
      {word.welcome}
    </Typography>
  );
};
